import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Clock, Zap, Shield, Users } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { challengeSchema } from "@/lib/form-validation";
import { SiteLayout } from "@/layouts/site-layout";
import { SEO } from "@/components/SEO";

const Challenge = () => {
  const [email, setEmail] = useState("");
  const [idea, setIdea] = useState("");
  const [newsletterEmail, setNewsletterEmail] = useState("");
  const [isSubmitOpen, setIsSubmitOpen] = useState(false);

  const handleSubmitIdea = async () => {
    // Validate using Zod schema
    const validation = challengeSchema.safeParse({ email, idea });
    
    if (!validation.success) {
      toast.error(validation.error.errors[0].message);
      return;
    }

    const validData = validation.data;

    try {
      // Use rate-limited edge function
      const { data: response, error } = await supabase.functions.invoke('submit-form', {
        body: {
          email: validData.email,
          type: 'challenge',
          data: {
            email: validData.email,
            idea_submission: validData.idea,
            status: 'new_submission',
          }
        }
      });

      if (error || !response?.success) {
        toast.error(response?.error || "Failed to submit idea. Please try again.");
        return;
      }

      toast.success("Idea submitted! We'll review and get back to you within 24 hours.");
      setIsSubmitOpen(false);
      setEmail("");
      setIdea("");
    } catch (error) {
      toast.error("An unexpected error occurred. Please try again.");
    }
  };

  const handleNewsletterSubmit = () => {
    if (!newsletterEmail) {
      toast.error("Please enter your email");
      return;
    }
    console.log("Newsletter signup:", newsletterEmail);
    toast.success("Subscribed! Welcome to the Build Week Newsletter.");
    setNewsletterEmail("");
  };

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "72-Hour Flash MVP Build",
    "description": "Get a working MVP prototype in 48 hours for your startup. Flash MVP build service for rapid validation.",
    "provider": {
      "@type": "Organization",
      "name": "OneWeekMVPs"
    },
    "offers": {
      "@type": "Offer",
      "description": "24-48h MVP prototype service for design agencies and founders"
    }
  };

  return (
    <SiteLayout>
      <SEO
        title="72-Hour Flash MVP Build | Rapid MVP Prototype Service in 48 Hours"
        description="Get your MVP in 72 hours or it's free. Rapid prototype service with 24-48h MVP prototype for design agencies. Flash MVP build service for early validation. AI-supervised rapid MVP prototype in one weekend."
        keywords="rapid prototype service, flash MVP build, 24-48h MVP prototype service for design agencies, rapid build MVP for early validation, get a working MVP prototype in 48 hours for your startup, flash MVP build service for agency partners to upsell clients, AI-supervised rapid MVP prototype in one weekend"
        canonicalUrl={`${typeof window !== 'undefined' ? window.location.origin : 'https://oneweekmvps.com'}/challenge`}
        structuredData={structuredData}
      />
      {/* Hero Section */}
      <section className="pt-32 pb-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-accent/5 to-transparent" />

        <div className="container mx-auto px-6 lg:px-12 relative">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="font-heading text-6xl md:text-7xl mb-6 text-foreground">
              Get your MVP in 72 hours.
              <span className="text-accent"> Or it's free.</span>
            </h1>
            <p className="text-xl md:text-2xl text-muted-foreground mb-8 max-w-3xl mx-auto">
              This isn't a service. It's a challenge. We're so confident we can build and launch your idea in 3 days, we'll give you a full refund if we're late.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
              <Dialog open={isSubmitOpen} onOpenChange={setIsSubmitOpen}>
                <DialogTrigger asChild>
                  <Button size="lg" className="text-lg px-8">
                    Start the Challenge
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl">
                  <DialogHeader>
                    <DialogTitle className="text-2xl">Submit Your Idea</DialogTitle>
                    <DialogDescription>
                      Tell us about your MVP idea and we'll validate if it's a fit for the 72-hour sprint.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 pt-4">
                    <div>
                      <Label htmlFor="challenge-email">Your Email</Label>
                      <Input
                        id="challenge-email"
                        type="email"
                        placeholder="you@company.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                      />
                    </div>
                    <div>
                      <Label htmlFor="idea">Your Idea</Label>
                      <Textarea
                        id="idea"
                        placeholder="Describe your MVP idea, core features, and target users..."
                        rows={8}
                        value={idea}
                        onChange={(e) => setIdea(e.target.value)}
                      />
                    </div>
                    <Button onClick={handleSubmitIdea} className="w-full" size="lg">
                      Submit My Idea
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>

              <Button variant="outline" size="lg" className="text-lg px-8">
                Join the Waitlist <span className="ml-2 text-accent">(4 Slots Left)</span>
              </Button>
            </div>

            {/* Live Stats */}
            <div className="grid grid-cols-3 gap-8 max-w-2xl mx-auto">
              <div>
                <div className="text-4xl font-heading text-accent mb-2">100+</div>
                <div className="text-sm text-muted-foreground">MVPs Built</div>
              </div>
              <div>
                <div className="text-4xl font-heading text-accent mb-2">4.2</div>
                <div className="text-sm text-muted-foreground">Days Average</div>
              </div>
              <div>
                <div className="text-4xl font-heading text-accent mb-2">98%</div>
                <div className="text-sm text-muted-foreground">Success Rate</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 border-t border-border">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <h2 className="font-heading text-5xl mb-4">Our High-Speed Sprint</h2>
            <p className="text-xl text-muted-foreground">Five steps from idea to launch</p>
          </div>

          <div className="max-w-4xl mx-auto space-y-8">
            {[
              { step: 1, title: "Submit Your Idea", desc: "Tell us your core concept." },
              { step: 2, title: "Get Validated (24h)", desc: "We confirm if it's a fit for the 72-hour sprint." },
              { step: 3, title: "See Your Prototype (24h)", desc: "You get a first-look prototype video. This offer expires." },
              { step: 4, title: "Approve & Pay", desc: "Lock in your slot. The clock starts." },
              { step: 5, title: "Launch in 72 Hours", desc: "Receive your production-ready, deployed MVP." }
            ].map((item) => (
              <Card key={item.step} className="p-6 border-accent/20">
                <div className="flex items-start gap-6">
                  <div className="flex-shrink-0 w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center">
                    <span className="text-2xl font-heading text-accent">{item.step}</span>
                  </div>
                  <div>
                    <h3 className="font-heading text-2xl mb-2">{item.title}</h3>
                    <p className="text-muted-foreground">{item.desc}</p>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Offer CTA Block */}
      <section className="py-20 border-t border-border bg-accent/5">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-3xl mx-auto text-center">
            <Clock className="w-16 h-16 text-accent mx-auto mb-6" />
            <h2 className="font-heading text-4xl md:text-5xl mb-6">
              Get Your 24-Hour Prototype Preview
            </h2>
            <p className="text-xl text-muted-foreground mb-8">
              Submit your idea. If it's a fit, we'll build and send a private prototype video within 24 hours. This offer is valid for 48 hours before the slot is given to the next person in line.
            </p>
            <Button size="lg" className="text-lg px-8" onClick={() => setIsSubmitOpen(true)}>
              Submit My Idea
            </Button>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 border-t border-border">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <h2 className="font-heading text-5xl mb-4">No Fluff. Just Launch-Ready.</h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-4">
              All 72-hour Flashbuilds include: Auth, Payments, Database, Admin, and Pro Design.
            </p>
            <p className="text-muted-foreground">
              Add-ons like AI, Analytics, and 3rd-Party Integrations are available.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {[
              { icon: Zap, title: "Lightning Fast", desc: "72 hours from approval to deployment" },
              { icon: Shield, title: "Quality Guaranteed", desc: "Production-ready code, not prototypes" },
              { icon: Users, title: "Full Stack", desc: "Frontend, backend, and deployment included" },
              { icon: Clock, title: "On-Time Delivery", desc: "Or you get a 100% refund" }
            ].map((feature, i) => (
              <Card key={i} className="p-6 text-center border-accent/20">
                <feature.icon className="w-12 h-12 text-accent mx-auto mb-4" />
                <h3 className="font-heading text-xl mb-2">{feature.title}</h3>
                <p className="text-sm text-muted-foreground">{feature.desc}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Guarantee Section */}
      <section className="py-20 border-t border-border bg-card">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-3xl mx-auto text-center">
            <Shield className="w-16 h-16 text-accent mx-auto mb-6" />
            <h2 className="font-heading text-4xl md:text-5xl mb-6">
              The Flashbuild Guarantee
            </h2>
            <p className="text-xl text-muted-foreground mb-8">
              Simple: We deliver your scoped MVP in 72 hours, or you get a 100% refund. No questions asked. We believe in speed and accountability.
            </p>
          </div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="py-20 border-t border-border">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="font-heading text-4xl mb-4">The Build Week Newsletter</h2>
            <p className="text-lg text-muted-foreground mb-8">
              Stay in the loop with tips, frameworks, and case studies from our weekly sprints.
            </p>
            <div className="flex gap-4 max-w-md mx-auto">
              <Input
                type="email"
                placeholder="Enter your email"
                value={newsletterEmail}
                onChange={(e) => setNewsletterEmail(e.target.value)}
              />
              <Button onClick={handleNewsletterSubmit}>Subscribe</Button>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 border-t border-border bg-accent/5">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="font-heading text-5xl mb-8">Challenge Accepted?</h2>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" className="text-lg px-8" onClick={() => setIsSubmitOpen(true)}>
                Start My 72-Hour Challenge
              </Button>
              <Button variant="outline" size="lg" className="text-lg px-8">
                Join Waitlist
              </Button>
            </div>
          </div>
        </div>
      </section>

    </SiteLayout>
  );
};

export default Challenge;
